import type {
  OperationsApprovalProposal,
  OperationsCaseCreate,
  OperationsEvidenceCreate,
  PlatformIncidentCreate,
} from "@gobro/contracts";
import type {
  Approval,
  AuditEvent,
  CaseDetail,
  FeatureControl,
  Incident,
  Locale,
  OperationsCase,
  OperationsOrder,
  Operator,
  PasskeyCredential,
  RegisteredPasskey,
  Summary,
} from "./domain";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem?: Record<string, unknown>,
  ) {
    super(message);
  }
}

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: "DELETE" | "GET" | "POST" | "PUT";
}

export class OperationsApi {
  constructor(readonly baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1") {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response: Response;
    try {
      const init: RequestInit = {
        credentials: "include",
        headers: {
          accept: "application/json",
          ...(options.body === undefined ? {} : { "content-type": "application/json" }),
          ...options.headers,
        },
        method: options.method ?? "GET",
      };
      if (options.body !== undefined) init.body = JSON.stringify(options.body);
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch (error) {
      throw new ApiError(error instanceof Error ? error.message : "Network unavailable", 0);
    }
    const contentType = response.headers.get("content-type") ?? "";
    const data = contentType.includes("json") ? await response.json() : await response.text();
    if (!response.ok) {
      const problem =
        typeof data === "object" && data !== null ? (data as Record<string, unknown>) : undefined;
      const detail = problem && typeof problem.detail === "string" ? problem.detail : String(data);
      throw new ApiError(detail || `HTTP ${response.status}`, response.status, problem);
    }
    return data as T;
  }

  me() {
    return this.request<Operator>("/auth/me");
  }

  requestOtp(phoneE164: string) {
    return this.request<{ challengeId: string }>("/auth/otp/request", {
      body: { phoneE164, purpose: "sign_in" },
      method: "POST",
    });
  }

  verifyOtp(challengeId: string, code: string) {
    return this.request<{ user: Operator }>("/auth/otp/verify", {
      body: { challengeId, clientType: "web", code, deviceName: "GoBro Operations" },
      method: "POST",
    });
  }

  passkeys() {
    return this.request<PasskeyCredential[]>("/auth/passkeys");
  }

  async registerPasskey(name: string): Promise<RegisteredPasskey> {
    assertPasskeySupport();
    const ceremony = await this.request<{
      ceremonyId: string;
      publicKey: PublicKeyCredentialCreationOptionsJSON;
    }>("/auth/passkeys/registration/options", { method: "POST" });
    const credential = (await navigator.credentials.create({
      publicKey: creationOptionsFromJson(ceremony.publicKey),
    })) as PublicKeyCredential | null;
    if (!credential) throw new ApiError("Passkey registration was cancelled", 0);
    const response = credential.response as AuthenticatorAttestationResponse;
    return this.request<RegisteredPasskey>("/auth/passkeys/registration/verify", {
      body: {
        ceremonyId: ceremony.ceremonyId,
        credential: {
          authenticatorAttachment: credential.authenticatorAttachment,
          clientExtensionResults: credential.getClientExtensionResults(),
          id: credential.id,
          rawId: toBase64Url(credential.rawId),
          response: {
            attestationObject: toBase64Url(response.attestationObject),
            clientDataJSON: toBase64Url(response.clientDataJSON),
            transports:
              typeof response.getTransports === "function" ? response.getTransports() : [],
          },
          type: "public-key",
        },
        name,
      },
      method: "POST",
    });
  }

  revokePasskey(credentialId: string) {
    return this.request<void>(`/auth/passkeys/${credentialId}`, { method: "DELETE" });
  }

  async verifyPasskey(): Promise<{ mfaVerified: true }> {
    assertPasskeySupport();
    const ceremony = await this.request<{
      ceremonyId: string;
      publicKey: PublicKeyCredentialRequestOptionsJSON;
    }>("/auth/passkeys/authentication/options", { method: "POST" });
    const credential = (await navigator.credentials.get({
      publicKey: requestOptionsFromJson(ceremony.publicKey),
    })) as PublicKeyCredential | null;
    if (!credential) throw new ApiError("Passkey verification was cancelled", 0);
    const response = credential.response as AuthenticatorAssertionResponse;
    return this.request<{ mfaVerified: true }>("/auth/passkeys/authentication/verify", {
      body: {
        ceremonyId: ceremony.ceremonyId,
        credential: {
          clientExtensionResults: credential.getClientExtensionResults(),
          id: credential.id,
          rawId: toBase64Url(credential.rawId),
          response: {
            authenticatorData: toBase64Url(response.authenticatorData),
            clientDataJSON: toBase64Url(response.clientDataJSON),
            signature: toBase64Url(response.signature),
            userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
          },
          type: "public-key",
        },
      },
      method: "POST",
    });
  }

  summary() {
    return this.request<Summary>("/operations/summary");
  }

  orders(q = "") {
    const params = new URLSearchParams({ limit: "200" });
    if (q.trim().length >= 2) params.set("q", q.trim());
    return this.request<{ items: OperationsOrder[] }>(`/operations/orders?${params}`);
  }

  order(id: string, perspective: "courier" | "customer" | "merchant") {
    return this.request<Record<string, unknown>>(
      `/operations/orders/${id}?perspective=${perspective}`,
    );
  }

  cases(q = "") {
    const params = new URLSearchParams({ limit: "100" });
    if (q.trim().length >= 2) params.set("q", q.trim());
    return this.request<{ items: OperationsCase[] }>(`/operations/cases?${params}`);
  }

  case(id: string) {
    return this.request<CaseDetail>(`/operations/cases/${id}`);
  }

  createCase(input: OperationsCaseCreate) {
    return this.request<OperationsCase>("/operations/cases", { body: input, method: "POST" });
  }

  updateCase(
    id: string,
    input: {
      expectedVersion: number;
      note?: string;
      priority?: OperationsCase["priority"];
      resolutionCode?: string | null;
      status?: OperationsCase["status"];
    },
  ) {
    return this.request<OperationsCase>(`/operations/cases/${id}`, {
      body: input,
      method: "PUT",
    });
  }

  addEvidence(id: string, input: OperationsEvidenceCreate) {
    return this.request(`/operations/cases/${id}/evidence`, { body: input, method: "POST" });
  }

  approvals() {
    return this.request<{ items: Approval[] }>("/operations/approvals?limit=100");
  }

  approval(id: string) {
    return this.request<Approval>(`/operations/approvals/${id}`);
  }

  proposeApproval(input: OperationsApprovalProposal) {
    return this.request<Approval>("/operations/approvals", {
      body: input,
      headers: { "idempotency-key": `ops-${crypto.randomUUID()}` },
      method: "POST",
    });
  }

  decideApproval(
    id: string,
    action: "approve" | "reject" | "retry",
    version: number,
    reason: string,
  ) {
    return this.request<Approval>(`/operations/approvals/${id}/${action}`, {
      body: { expectedVersion: version, reason },
      method: "POST",
    });
  }

  controls() {
    return this.request<{ items: FeatureControl[] }>("/operations/feature-controls");
  }

  updateControl(control: FeatureControl, enabled: boolean, reason: string) {
    return this.request<FeatureControl>(`/operations/feature-controls/${control.featureKey}`, {
      body: { enabled, expectedVersion: control.version, reason },
      method: "PUT",
    });
  }

  incidents() {
    return this.request<{ items: Incident[] }>("/operations/incidents");
  }

  createIncident(input: PlatformIncidentCreate) {
    return this.request<Incident>("/operations/incidents", { body: input, method: "POST" });
  }

  updateIncident(id: string, version: number, status: "active" | "resolved") {
    return this.request<Incident>(`/operations/incidents/${id}`, {
      body: { expectedVersion: version, status },
      method: "PUT",
    });
  }

  audit(action = "") {
    const params = new URLSearchParams({ format: "json", limit: "250" });
    if (action.trim().length >= 2) params.set("action", action.trim());
    return this.request<{ items: AuditEvent[] }>(`/operations/audit?${params}`);
  }

  async downloadAudit(action = ""): Promise<void> {
    const params = new URLSearchParams({ format: "csv", limit: "1000" });
    if (action.trim().length >= 2) params.set("action", action.trim());
    const response = await fetch(`${this.baseUrl}/operations/audit?${params}`, {
      credentials: "include",
      headers: { accept: "text/csv" },
    });
    if (!response.ok) throw new ApiError("Audit export failed", response.status);
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `gobro-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  events(afterCursor: number) {
    return this.request<{
      cursor: number;
      events: Array<{ cursor: number; eventType: string }>;
    }>(`/operations/events?afterCursor=${afterCursor}&limit=500`);
  }

  publicStatus(locale: Locale) {
    return this.request<{ incidents: Incident[] }>(`/status?audience=operations&locale=${locale}`);
  }
}

interface PublicKeyCredentialRequestOptionsJSON {
  allowCredentials?: Array<{
    id: string;
    transports?: AuthenticatorTransport[];
    type: PublicKeyCredentialType;
  }>;
  challenge: string;
  extensions?: AuthenticationExtensionsClientInputs;
  rpId?: string;
  timeout?: number;
  userVerification?: UserVerificationRequirement;
}

interface PublicKeyCredentialCreationOptionsJSON {
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  challenge: string;
  excludeCredentials?: Array<{
    id: string;
    transports?: AuthenticatorTransport[];
    type: PublicKeyCredentialType;
  }>;
  extensions?: AuthenticationExtensionsClientInputs;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  rp: PublicKeyCredentialRpEntity;
  timeout?: number;
  user: {
    displayName: string;
    id: string;
    name: string;
  };
}

function assertPasskeySupport(): void {
  if (!window.PublicKeyCredential || !navigator.credentials) {
    throw new ApiError("This browser does not support passkeys", 0);
  }
}

function requestOptionsFromJson(
  options: PublicKeyCredentialRequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  const result: PublicKeyCredentialRequestOptions = {
    challenge: fromBase64Url(options.challenge),
  };
  if (options.allowCredentials) {
    result.allowCredentials = options.allowCredentials.map((credential) => ({
      ...credential,
      id: fromBase64Url(credential.id),
    }));
  }
  if (options.extensions) result.extensions = options.extensions;
  if (options.rpId) result.rpId = options.rpId;
  if (options.timeout !== undefined) result.timeout = options.timeout;
  if (options.userVerification) result.userVerification = options.userVerification;
  return result;
}

function creationOptionsFromJson(
  options: PublicKeyCredentialCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  const result: PublicKeyCredentialCreationOptions = {
    challenge: fromBase64Url(options.challenge),
    pubKeyCredParams: options.pubKeyCredParams,
    rp: options.rp,
    user: {
      displayName: options.user.displayName,
      id: fromBase64Url(options.user.id),
      name: options.user.name,
    },
  };
  if (options.attestation) result.attestation = options.attestation;
  if (options.authenticatorSelection) {
    result.authenticatorSelection = options.authenticatorSelection;
  }
  if (options.excludeCredentials) {
    result.excludeCredentials = options.excludeCredentials.map((credential) => ({
      ...credential,
      id: fromBase64Url(credential.id),
    }));
  }
  if (options.extensions) result.extensions = options.extensions;
  if (options.timeout !== undefined) result.timeout = options.timeout;
  return result;
}

function fromBase64Url(value: string): ArrayBuffer {
  const base64 = value.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0)).buffer;
}

function toBase64Url(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}
